import React, { useCallback, useRef } from 'react'
import PropTypes from 'prop-types'
import AceEditor from 'react-ace'
import 'ace-builds/src-noconflict/mode-javascript'
import 'ace-builds/src-noconflict/mode-json'
import 'ace-builds/src-noconflict/theme-xcode'
import { validateSource } from '../../SourceEditor/utils/smartEndpointValidation'
import { TEST_MODES } from './testFixtures'

function TestConfig ({ test, selectedIndex, onConfigChange, classes }) {
  const assertionsEditorRef = useRef(null)

  const handleAssertionsChange = useCallback((value) => {
    onConfigChange(selectedIndex, 'assertions', value)
    if (assertionsEditorRef.current) {
      assertionsEditorRef.current.getSession().setAnnotations(validateSource(value))
    }
  }, [selectedIndex, onConfigChange])

  return (
    <div className={classes.configPanel}>
      <div className={classes.fieldRow}>
        <div className={classes.fieldGroup} style={{ flex: 1 }}>
          <label className={classes.fieldLabel}>Name</label>
          <input
            className={classes.nameInput}
            value={test.name}
            onChange={(e) => onConfigChange(selectedIndex, 'name', e.target.value)}
            style={{ width: '100%' }}
          />
        </div>
        <div className={classes.fieldGroup}>
          <label className={classes.fieldLabel}>Mode</label>
          <div className={classes.modeSelector}>
            {TEST_MODES.map(mode => (
              <button
                key={mode.id}
                className={`${classes.modeOption} ${test.testMode === mode.id ? classes.modeOptionActive : ''}`}
                style={test.testMode === mode.id ? { backgroundColor: mode.color } : undefined}
                onClick={() => onConfigChange(selectedIndex, 'testMode', mode.id)}
              >
                {mode.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {(test.modeOverride || test.policyOverride != null) && (
        <div className={classes.overrideBadgeRow}>
          {test.modeOverride && (
            <span className={test.modeOverride === 'query' ? classes.overrideBadgeQuery : classes.overrideBadgeCommand}>
              Mode: {test.modeOverride === 'query' ? 'Read Only' : 'Read/Write'}
            </span>
          )}
          {test.policyOverride != null && (
            <span className={classes.overrideBadgePolicy}>
              Policy override ({test.policyOverride.length} {test.policyOverride.length === 1 ? 'entry' : 'entries'})
            </span>
          )}
        </div>
      )}

      <div className={classes.fieldGroup}>
        <label className={classes.fieldLabel}>Input</label>
        <div className={classes.aceWrapper}>
          <AceEditor
            key={`input-${selectedIndex}`}
            height='80px'
            width='100%'
            mode='json'
            theme='xcode'
            name={`test-input-${selectedIndex}`}
            value={test.input}
            onChange={(val) => onConfigChange(selectedIndex, 'input', val)}
            tabSize={2}
            fontSize={12}
            showPrintMargin={false}
            editorProps={{ $blockScrolling: true }}
            setOptions={{ useWorker: false, showLineNumbers: false }}
          />
        </div>
      </div>

      <div className={classes.fieldGroup}>
        <label className={classes.fieldLabel}>Mock Responses</label>
        <div className={classes.aceWrapper}>
          <AceEditor
            key={`responses-${selectedIndex}`}
            height='80px'
            width='100%'
            mode='json'
            theme='xcode'
            name={`test-responses-${selectedIndex}`}
            value={test.responses}
            onChange={(val) => onConfigChange(selectedIndex, 'responses', val)}
            tabSize={2}
            fontSize={12}
            showPrintMargin={false}
            editorProps={{ $blockScrolling: true }}
            setOptions={{ useWorker: false, showLineNumbers: false }}
          />
        </div>
      </div>

      <div className={classes.fieldGroup}>
        <label className={classes.fieldLabel}>Assertions</label>
        <div className={classes.aceWrapper}>
          <AceEditor
            key={`assertions-${selectedIndex}`}
            height='80px'
            width='100%'
            mode='javascript'
            theme='xcode'
            name={`test-assertions-${selectedIndex}`}
            value={test.assertions}
            tabSize={2}
            fontSize={12}
            showPrintMargin={false}
            editorProps={{ $blockScrolling: true }}
            setOptions={{ useWorker: false, showLineNumbers: false }}
            onLoad={(editor) => {
              assertionsEditorRef.current = editor
              editor.getSession().setAnnotations(validateSource(test.assertions))
            }}
            onChange={handleAssertionsChange}
          />
        </div>
      </div>
    </div>
  )
}

TestConfig.propTypes = {
  test: PropTypes.object.isRequired,
  selectedIndex: PropTypes.number.isRequired,
  onConfigChange: PropTypes.func.isRequired,
  classes: PropTypes.object.isRequired
}

export default TestConfig
